(() => {
  const PRODUCT_SELECTOR = 'main-product-default';
  const WRAPPER_SELECTOR = '[data-biv-back-in-stock-wrapper]';
  const VARIANT_INPUT_SELECTOR = '[js-variant-input]';
  const MAIN_ATC_SELECTOR = 'product-block-buy-buttons [js-buy-buttons] button[js-add-to-cart-button]';
  const LEGACY_TRIGGER_SELECTOR = '.BIS_trigger';
  const NATIVE_TRIGGER_SELECTOR = '#BIS_trigger, .BIS_trigger:not([data-biv-amp-fallback])';
  const FALLBACK_TRIGGER_SELECTOR = '[data-biv-amp-fallback]';

  const selectedControl = (root) => root?.querySelector(
    'input[js-variant-input][type="radio"]:checked, select[js-variant-input]',
  );

  const selectedVariantId = (root) => {
    const control = selectedControl(root);
    if (!control) return '';

    if (control.matches('select')) {
      return String(control.selectedOptions?.[0]?.dataset?.variantId || control.value || '');
    }

    return String(control.dataset.variantId || control.value || '');
  };

  const triggerIsVisible = (trigger) => {
    if (!trigger) return false;
    const styles = getComputedStyle(trigger);
    return styles.display !== 'none'
      && styles.visibility !== 'hidden'
      && trigger.getClientRects().length > 0;
  };

  const syncTriggers = (root, isVisible) => {
    if (!root) return;
    const wrapper = root.querySelector(WRAPPER_SELECTOR);
    if (!wrapper) return;

    const variantId = selectedVariantId(root);
    const nativeTrigger = wrapper.querySelector(NATIVE_TRIGGER_SELECTOR);
    const fallbackTrigger = wrapper.querySelector(FALLBACK_TRIGGER_SELECTOR);

    if (nativeTrigger && variantId) {
      nativeTrigger.setAttribute('data-variant-id', variantId);
    }

    if (isVisible && nativeTrigger) {
      nativeTrigger.style.removeProperty('display');
      nativeTrigger.removeAttribute('aria-hidden');
    }

    const nativeUsable = isVisible && triggerIsVisible(nativeTrigger);

    if (fallbackTrigger) {
      if (variantId) {
        fallbackTrigger.setAttribute('data-variant-id', variantId);
      }

      if (isVisible && !nativeUsable) {
        fallbackTrigger.style.removeProperty('display');
        fallbackTrigger.removeAttribute('aria-hidden');
      } else {
        fallbackTrigger.style.setProperty('display', 'none', 'important');
        fallbackTrigger.setAttribute('aria-hidden', 'true');
      }
    }
  };

  const setWrapperVisibility = (root, isVisible) => {
    if (!root) return;
    const wrapper = root.querySelector(WRAPPER_SELECTOR);
    if (!wrapper) return;

    if (isVisible) {
      wrapper.style.removeProperty('display');
      wrapper.removeAttribute('aria-hidden');
    } else {
      wrapper.style.setProperty('display', 'none', 'important');
      wrapper.setAttribute('aria-hidden', 'true');
    }

    syncTriggers(root, isVisible);
  };

  const controlIsUnavailable = (control) => {
    if (!control) return false;

    if (control.matches('input')) {
      return control.classList.contains('disabled');
    }

    if (control.matches('select')) {
      const selectedOption = control.selectedOptions && control.selectedOptions[0];
      return Boolean(selectedOption && selectedOption.disabled);
    }

    return false;
  };

  const syncFromRenderedBuyButton = (root) => {
    if (!root) return;

    const button = root.querySelector(MAIN_ATC_SELECTOR);
    if (!button) return;

    // Eclipse renders the main ATC disabled when the selected variant is Sold Out.
    // This is driven by variant.available in Liquid, so use the same rendered source
    // of truth rather than depending on the visual state of the variant chip.
    setWrapperVisibility(root, button.disabled);
  };

  const removeLegacyTrigger = (trigger) => {
    if (!trigger || !trigger.matches(LEGACY_TRIGGER_SELECTOR)) return;

    // The wanted AMP App Block and BIV fallback both live inside the dedicated wrapper.
    // Only remove AMP's legacy injected CTA when it appears outside that wrapper.
    if (trigger.closest(WRAPPER_SELECTOR)) return;

    trigger.remove();
  };

  const removeLegacyTriggerNodes = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches(LEGACY_TRIGGER_SELECTOR)) {
      removeLegacyTrigger(node);
    }

    node.querySelectorAll(LEGACY_TRIGGER_SELECTOR).forEach(removeLegacyTrigger);
  };

  const pruneLegacyTriggers = () => {
    document.querySelectorAll(LEGACY_TRIGGER_SELECTOR).forEach(removeLegacyTrigger);
  };

  const syncAll = () => {
    document.querySelectorAll(PRODUCT_SELECTOR).forEach(syncFromRenderedBuyButton);
  };

  const startLegacyTriggerObserver = () => {
    pruneLegacyTriggers();
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      let addedContent = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          addedContent = true;
          removeLegacyTriggerNodes(node);
        });
      });

      // If AMP injects its native trigger after a variant repaint, prefer it and
      // automatically hide the BIV fallback on the next sync.
      if (addedContent) syncAll();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  // Fast provisional update while Eclipse fetches the selected variant markup.
  // Unavailable chips remain selectable but retain class="disabled" as a data signal.
  document.addEventListener('change', (event) => {
    const control = event.target && event.target.closest
      ? event.target.closest(VARIANT_INPUT_SELECTOR)
      : null;

    if (!control) return;

    const root = control.closest(PRODUCT_SELECTOR);
    setWrapperVisibility(root, controlIsUnavailable(control));
  });

  // Eclipse dispatches this after its new variant-dependent ATC markup is in the DOM.
  // The event itself does not bubble, so listen in capture phase at document level.
  document.addEventListener('product:variant:changed', (event) => {
    const root = event.target && event.target.closest
      ? event.target.closest(PRODUCT_SELECTOR)
      : null;

    if (root) syncFromRenderedBuyButton(root);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target && event.target.querySelector
      ? event.target.querySelector(PRODUCT_SELECTOR)
      : null;

    if (root) syncFromRenderedBuyButton(root);
    pruneLegacyTriggers();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncAll();
      startLegacyTriggerObserver();
    }, { once: true });
  } else {
    syncAll();
    startLegacyTriggerObserver();
  }
})();
